import React, { useState } from 'react';
import { Field, Modal, Spinner } from '../../components/ui';
import { CARD_STATUSES, EXTRA_PERSONS_PER_CARD, fmtDate } from '../../utils/helpers';
import { GUEST_TYPES } from './useCards';

// Desktop Cards — what to ORDER (per card type, extra cards included), who
// delivers which card, and the printable sheet of names. Logic: useCards.
export default function CardsDesktop({ vm }) {
  const {
    canView, canEdit, canManageTypes, cards, counts, loading, functions, event, fnName,
    fnFilter, setFnFilter, typeFilter, setTypeFilter, cardTypeFilter, setCardTypeFilter,
    delivererFilter, setDelivererFilter, statusFilter, setStatusFilter, q, setQ,
    cardTypes, orderTotals, withoutCard, extraTotals, byDeliverer, delivererNames,
    setExtraCards, setExtraPersons, setDeliverer, setStatus,
    selected, toggleSelect, allVisibleSelected, toggleSelectAll, selectedCount, bulkDeliverer, bulkStatus,
    editing, setEditing, cardForm, setCardF, saveCard, saving, openCard,
    typeForm, openTypeAdd, openTypeEdit, closeType, setTypeF, toggleTypeFunction, toggleTypeExtraFunction, saveType, deleteType, addDefaultTypes,
    CARD_KINDS, typeFnIds, print,
  } = vm;
  const [bulkName, setBulkName] = useState('');

  if (!canView) {
    return (
      <div>
        <div className="page-head"><h1>Cards</h1></div>
        <div className="card card-pad empty">You do not have access to the guest list of this event.</div>
      </div>
    );
  }
  // Commit a typed value on blur / Enter (inputs are uncontrolled, keyed by value).
  const commitOnEnter = (e) => { if (e.key === 'Enter') e.currentTarget.blur(); };

  return (
    <div className="cards-page">
      <div className="page-head no-print">
        <h1>Cards</h1>
        <span className="muted">{counts.cards} cards · {counts.family} families · {counts.business} business</span>
        <div className="spacer" />
        <button className="btn light" onClick={print}>🖨 Print names</button>
      </div>

      {/* ── What to order ─────────────────────────────────────────── */}
      <div className="card card-pad cards-order no-print">
        <div className="cards-order-head">
          <strong>Cards to order</strong>
          {canManageTypes && <button className="btn light sm" onClick={openTypeAdd}>+ Card type</button>}
        </div>
        {cardTypes.length === 0 ? (
          <div className="muted" style={{ fontSize: 13 }}>
            No card types yet. A card type says which functions a card is for, e.g. the <strong>Reception card</strong> for everyone
            and a <strong>Thread Ceremony insert</strong> (extra paper inside) for those invited to the thread ceremony.
            {canManageTypes && functions.length > 0 && (
              <div style={{ marginTop: 8 }}><button className="btn primary sm" onClick={addDefaultTypes}>Set up Reception card + Thread Ceremony insert</button></div>
            )}
          </div>
        ) : (
          <div className="cards-order-tiles">
            {orderTotals.map(({ type, households, extra, total }) => (
              <div key={type.CardTypeId} className={'cards-order-tile' + (String(cardTypeFilter) === String(type.CardTypeId) ? ' on' : '')}
                onClick={() => setCardTypeFilter(String(cardTypeFilter) === String(type.CardTypeId) ? '' : String(type.CardTypeId))}
                title="Show only the households that get this card">
                <div className="cards-order-total">{total}</div>
                <div className="cards-order-name">{type.CardTypeName} <span className="chip">{type.Kind}</span></div>
                <div className="muted">{households} households{extra ? ` + ${extra} extra cards` : ''}</div>
                {Number(type.ForExtraCards) === 1 && (
                  <div className="muted cards-order-fns">Extra-card guests: {String(type.ExtraFunctionIds || type.FunctionIds || '').split(',').map(fnName).filter(Boolean).join(', ')}</div>
                )}
                <div className="muted cards-order-fns">{typeFnIds(type).map(fnName).filter(Boolean).join(', ')}</div>
                {canManageTypes && (
                  <div className="cards-order-tools" onClick={e => e.stopPropagation()}>
                    <button className="icon-btn" title="Edit" onClick={() => openTypeEdit(type)}>✎</button>
                    <button className="icon-btn danger" title="Remove" onClick={() => deleteType(type)}>🗑</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Extra cards: <strong>{extraTotals.cards}</strong> → <strong>{extraTotals.persons}</strong> persons added to the Reception headcount
          ({EXTRA_PERSONS_PER_CARD} per card unless a number is typed).
          {withoutCard > 0 && <> · <span style={{ color: 'var(--danger)' }}>{withoutCard} households are in no card type</span></>}
        </div>
        {byDeliverer.length > 0 && (
          <div className="cards-deliverers">
            <span className="muted">Delivery:</span>
            {byDeliverer.map(d => (
              <button key={d.name} className={'chip-toggle' + (delivererFilter === d.name ? ' on' : '')}
                onClick={() => setDelivererFilter(delivererFilter === d.name ? '' : d.name)}>
                {d.name === '—' ? 'Not assigned' : d.name} · {d.delivered}/{d.cards}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Filters ───────────────────────────────────────────────── */}
      <div className="cards-filters no-print">
        <select value={fnFilter} onChange={e => setFnFilter(e.target.value)} title="Filter by function">
          <option value="">All functions</option>
          {functions.map(f => <option key={f.FunctionId} value={f.FunctionId}>{f.FunctionName}</option>)}
        </select>
        <select value={cardTypeFilter} onChange={e => setCardTypeFilter(e.target.value)} title="Card type">
          <option value="">All card types</option>
          {cardTypes.map(t => <option key={t.CardTypeId} value={t.CardTypeId}>{t.CardTypeName}</option>)}
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} title="Family or business">
          <option value="">Family + business</option>
          {GUEST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={delivererFilter} onChange={e => setDelivererFilter(e.target.value)} title="Delivered by">
          <option value="">Anyone delivering</option>
          <option value="—">Not assigned</option>
          {delivererNames.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} title="Status">
          <option value="">Pending + delivered</option>
          {CARD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="search" placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} />
      </div>

      {canEdit && selectedCount > 0 && (
        <div className="cards-bulk no-print">
          <strong>{selectedCount} selected</strong>
          <input list="card-deliverers" placeholder="Delivered by…" value={bulkName} onChange={e => setBulkName(e.target.value)} />
          <button className="btn primary sm" onClick={() => { bulkDeliverer(bulkName); setBulkName(''); }}>Assign</button>
          <button className="btn light sm" onClick={() => bulkStatus('Delivered')}>Mark delivered</button>
          <button className="btn light sm" onClick={() => bulkStatus('Pending')}>Mark pending</button>
        </div>
      )}
      <datalist id="card-deliverers">{delivererNames.map(n => <option key={n} value={n} />)}</datalist>

      {/* ── One row per card ──────────────────────────────────────── */}
      {loading ? <div className="card card-pad"><Spinner /></div>
        : cards.length === 0 ? <div className="card card-pad empty">No cards match.</div> : (
          <div className="card table-wrap no-print">
            <table className="tbl compact cards-table">
              <thead>
                <tr>
                  {canEdit && <th><input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} title="Select all shown" /></th>}
                  <th className="num">#</th>
                  <th className="grow">Name on card</th>
                  <th className="num" title="People on this card">People</th>
                  <th>Cards</th>
                  <th className="num" title="Extra cards this head asked for (names unknown, Reception only)">Extra cards</th>
                  <th className="num" title={`Persons on the extra cards — blank = ${EXTRA_PERSONS_PER_CARD} per card`}>Extra persons</th>
                  <th>Delivered by</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {cards.map((c, i) => (
                  <tr key={c.key} className={selected.has(c.key) ? 'selected' : undefined}>
                    {canEdit && <td><input type="checkbox" checked={selected.has(c.key)} onChange={() => toggleSelect(c.key)} /></td>}
                    <td className="num muted">{i + 1}</td>
                    <td className="grow">
                      <button className="linklike cards-name" disabled={!canEdit} onClick={() => openCard(c)} title="Edit the name on the card">{c.name}</button>
                      <div className="sub">{[c.subtitle, c.mobile, c.city].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td className="num" title={c.members.map(m => m.GuestName).join(', ')}>{c.persons}</td>
                    <td>
                      {c.cardTypes.length
                        ? c.cardTypes.map(t => <span key={t.CardTypeId} className={'chip ' + (t.Kind === 'Insert' ? 'warn' : 'ok')} style={{ marginRight: 4 }}>{t.CardTypeName}</span>)
                        : <span className="muted">{cardTypes.length ? 'none' : '—'}</span>}
                    </td>
                    <td className="num">
                      <input key={`x${c.key}${c.extraCards}`} className="cards-num" type="number" min="0" max="99" defaultValue={c.extraCards || ''}
                        disabled={!canEdit} placeholder="0" onBlur={e => setExtraCards(c, e.target.value)} onKeyDown={commitOnEnter} />
                    </td>
                    <td className="num">
                      <input key={`p${c.key}${c.extraPersonsTyped}${c.extraCards}`} className="cards-num" type="number" min="0" defaultValue={c.extraPersonsTyped || ''}
                        disabled={!canEdit || !c.extraCards} placeholder={c.extraCards ? String(c.extraPersons) : ''}
                        onBlur={e => setExtraPersons(c, e.target.value)} onKeyDown={commitOnEnter} />
                    </td>
                    <td>
                      <input key={`d${c.key}${c.deliveredBy}`} className="cards-deliverer" list="card-deliverers" defaultValue={c.deliveredBy}
                        disabled={!canEdit} placeholder="—" onBlur={e => setDeliverer(c, e.target.value)} onKeyDown={commitOnEnter} />
                    </td>
                    <td>
                      <button className={'chip ' + (c.status === 'Delivered' ? 'ok' : 'pend')} disabled={!canEdit}
                        title={c.status === 'Delivered' ? `Delivered ${fmtDate(c.deliveredDate)} — click to set Pending` : 'Click when delivered'}
                        onClick={() => setStatus(c, c.status === 'Delivered' ? 'Pending' : 'Delivered')}>
                        {c.status === 'Delivered' ? `✓ ${fmtDate(c.deliveredDate)}` : 'Pending'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {/* ── Printable sheet of names (print only) ─────────────────── */}
      <div className="print-title" style={{ display: 'none' }}>{event?.EventName}</div>
      <div className="card-sheet cards-print-sheet">
        {cards.map((c, i) => (
          <div key={c.key} className="guest-card">
            <div className="guest-card-no">{i + 1}</div>
            <div className="guest-card-name">{c.name}</div>
            {c.subtitle && <div className="guest-card-sub">{c.subtitle}</div>}
            <div className="guest-card-meta">
              {c.mobile}{c.city ? ` · ${c.city}` : ''}{c.extraCards ? ` · +${c.extraCards} extra` : ''}{c.deliveredBy ? ` · by ${c.deliveredBy}` : ''}
            </div>
            {c.cardTypes.length > 0 && <div className="guest-card-fns">{c.cardTypes.map(t => t.CardTypeName).join(' + ')}</div>}
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={`Card · ${editing.name}`} onClose={() => setEditing(null)}
          footer={<>
            <button className="btn light" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn primary" disabled={saving} onClick={saveCard}>{saving ? 'Saving…' : 'Save'}</button>
          </>}>
          <div className="form-grid">
            <Field label="Card for" type="select" value={cardForm.GuestType} options={GUEST_TYPES} onChange={(v) => setCardF('GuestType', v)} />
            {cardForm.GuestType === 'Business' && (
              <Field label="Company name" value={cardForm.CompanyName} onChange={(v) => setCardF('CompanyName', v)} />
            )}
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Name on the card (leave blank to use the head's name)" value={cardForm.CardName}
                onChange={(v) => setCardF('CardName', v)} placeholder="e.g. Shri & Smt. Haresh Abichandani & Family" />
            </div>
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            Covers {editing.members.length} people: {editing.members.map(m => m.GuestName).filter(Boolean).join(', ')}.
          </div>
        </Modal>
      )}

      {typeForm && (
        <Modal title={typeForm.CardTypeId ? 'Edit card type' : 'New card type'} onClose={closeType}
          footer={<><button className="btn light" onClick={closeType}>Cancel</button><button className="btn primary" onClick={saveType}>Save</button></>}>
          <div className="form-grid">
            <Field label="Name" value={typeForm.CardTypeName} onChange={(v) => setTypeF('CardTypeName', v)} placeholder="e.g. Reception Card" />
            <Field label="Kind" type="select" value={typeForm.Kind} options={CARD_KINDS} onChange={(v) => setTypeF('Kind', v)} />
          </div>
          <div className="field-label">For guests invited to any of</div>
          <div className="guest-fn-checks" style={{ marginBottom: 10 }}>
            {functions.map(f => (
              <label key={f.FunctionId}>
                <input type="checkbox" checked={typeForm.FunctionIds.includes(String(f.FunctionId))} onChange={() => toggleTypeFunction(f.FunctionId)} />
                {f.FunctionName}
              </label>
            ))}
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={!!typeForm.ForExtraCards} onChange={e => setTypeF('ForExtraCards', e.target.checked ? 1 : 0)} />
            Extra cards are copies of this card
          </label>
          {!!typeForm.ForExtraCards && (
            <>
              <div className="field-label" style={{ marginTop: 10 }}>Extra-card guests come to (counted in their headcount)</div>
              <div className="guest-fn-checks">
                {functions.filter(f => typeForm.FunctionIds.includes(String(f.FunctionId))).map(f => (
                  <label key={f.FunctionId}>
                    <input type="checkbox" checked={typeForm.ExtraFunctionIds.includes(String(f.FunctionId))} onChange={() => toggleTypeExtraFunction(f.FunctionId)} />
                    {f.FunctionName}
                  </label>
                ))}
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
